/**
 * CombatSystem — Core combat logic, decoupled from rendering.
 *
 * Manages grid, initiative, action points, attacks, movement,
 * items, enemy AI, and combat state. Emits events via EventBus.
 *
 * @module CombatSystem
 */

import eventBus from '../core/EventBus.js';
import dataStore from './DataLoader.js';
import skillSystem from './SkillSystem.js';
import statusEffectsSystem from './StatusEffects.js';
import tileEffectsSystem from './TileEffects.js';
import lootSystem from './LootSystem.js';
import inventorySystem from './InventorySystem.js';
import companionSystem from './CompanionSystem.js';
import combatAI, { CombatAI } from './CombatAI.js';

/** Default AP per turn. */
const DEFAULT_AP = 5;

/** AP costs for various actions. */
const AP_COSTS = {
  basic: 2,
  strong: 3,
  quick: 1,
  move: 1,
  item: 1,
  defend: 2,
};

/** Attack type damage multipliers. */
const ATTACK_MULTIPLIERS = {
  basic: 1.0,
  strong: 1.5,
  quick: 0.6,
};

/**
 * Create a combatant from a Character entity.
 * @param {object} character - Character instance
 * @param {number} index - Party index
 * @returns {object} combatant
 */
function createPlayerCombatant(character, index) {
  return {
    id: character.id || `player_${index}`,
    name: character.name,
    type: 'player',
    hp: character.hp,
    maxHp: character.maxHp,
    mana: character.mana || 0,
    maxMana: character.maxMana || 0,
    ap: DEFAULT_AP,
    maxAp: DEFAULT_AP,
    attributes: { ...character.attributes },
    initiative: 0,
    gridX: 0,
    gridY: 0,
    alive: true,
    statusEffects: [],
    character, // Reference to the original Character
    behavior: null,
    defense: 10 + Math.floor((character.attributes.agi - 10) / 2),
    damage: character.equipment?.weapon?.baseStats?.damage
      || (8 + Math.floor((character.attributes.str - 10) / 2)),
    speed: character.attributes.agi,
  };
}

/**
 * Create a combatant from enemy data.
 * @param {object} enemyData - Enemy definition from enemies.json
 * @param {number} level - Enemy level
 * @param {number} index - Enemy index
 * @returns {object} combatant
 */
function createEnemyCombatant(enemyData, level, index) {
  // Scale stats with level
  const levelScale = 1 + (level - 1) * 0.1;
  const baseHp = Math.floor(enemyData.baseStats.hp * levelScale);
  const baseDamage = Math.floor(enemyData.baseStats.damage * levelScale);
  const baseDefense = Math.floor(enemyData.baseStats.defense * levelScale);

  return {
    id: `enemy_${enemyData.id}_${index}_${Date.now()}`,
    name: enemyData.name,
    type: 'enemy',
    hp: baseHp,
    maxHp: baseHp,
    mana: enemyData.baseStats.mana || 0,
    maxMana: enemyData.baseStats.mana || 0,
    ap: DEFAULT_AP,
    maxAp: DEFAULT_AP,
    attributes: {
      str: 10,
      agi: Math.floor(enemyData.baseStats.speed / 1.5) + 5,
      int: 10,
      vit: 10,
      wis: 10,
      cha: 10,
    },
    initiative: 0,
    gridX: 0,
    gridY: 0,
    alive: true,
    statusEffects: [],
    character: null,
    behavior: enemyData.behavior || 'melee',
    defense: 10 + baseDefense,
    damage: baseDamage,
    speed: enemyData.baseStats.speed || 10,
    enemyData, // Reference to original enemy definition
    level,
    loot: enemyData.loot || null,
    size: enemyData.size || 'medium',
    variant: enemyData.variant || null,
  };
}

class CombatSystem {
  constructor() {
    /** @type {object[][]} Grid: 2D array [row][col] = combatant | null */
    this.grid = [];

    /** @type {number} Grid rows */
    this.rows = 0;

    /** @type {number} Grid columns */
    this.cols = 0;

    /** @type {object[]} All combatants in initiative order */
    this.turnOrder = [];

    /** @type {number} Index into turnOrder for current turn */
    this.turnIndex = 0;

    /** @type {object[]} Player combatants */
    this.players = [];

    /** @type {object[]} Enemy combatants */
    this.enemies = [];

    /** @type {object[]} Companion combatants */
    this.companionCombatants = [];

    /** @type {string} Combat state: 'idle' | 'active' | 'playerTurn' | 'enemyTurn' | 'autoTurn' | 'companionTurn' | 'victory' | 'defeat' */
    this.state = 'idle';

    /** @type {string[]} Combat log messages */
    this.log = [];

    /** @type {number} Turn counter */
    this.turnCount = 0;

    /** @type {object|null} Reference to the party */
    this._party = null;

    /** @type {object|null} Accumulated rewards */
    this._rewards = null;

    /** Seeded RNG for deterministic testing */
    this._seed = Date.now();

    /** @type {number[]} Region level range for difficulty scaling */
    this._currentRegionLevelRange = [1, 3];
  }

  /**
   * Set the current region level range for difficulty scaling.
   * @param {number[]} range - [minLevel, maxLevel]
   */
  setRegionLevelRange(range) {
    this._currentRegionLevelRange = range || [1, 3];
  }

  /**
   * Roll a d20 using simple RNG.
   * @returns {number} 1-20
   */
  _rollD20() {
    return Math.floor(Math.random() * 20) + 1;
  }

  /**
   * Roll a d100.
   * @returns {number} 1-100
   */
  _rollD100() {
    return Math.floor(Math.random() * 100) + 1;
  }

  /**
   * Get attribute modifier: (value - 10) / 2, floored.
   * @param {number} value
   * @returns {number}
   */
  _getModifier(value) {
    return Math.floor((value - 10) / 2);
  }

  // ── Initialization ──────────────────────────────────

  /**
   * Start a new combat encounter.
   * @param {object} party - Party instance
   * @param {object[]} enemyDefs - Array of { id, name, level } enemy entries
   * @returns {boolean} true if combat started successfully
   */
  startCombat(party, enemyDefs) {
    this._party = party;
    this.log = [];
    this.turnCount = 0;
    this._rewards = null;

    // Initialize sub-systems
    statusEffectsSystem.init(this);
    tileEffectsSystem.init(this, statusEffectsSystem);
    tileEffectsSystem.reset();
    skillSystem.init(this, tileEffectsSystem, statusEffectsSystem);
    skillSystem.reset();

    // Create player combatants
    const members = party.members || party.getActiveMembers?.() || [];
    this.players = members.map((char, i) => createPlayerCombatant(char, i));

    // Create companion combatants from active companions
    this.companionCombatants = [];
    for (const player of this.players) {
      if (player.character) {
        const companion = companionSystem.getCompanionForCharacter(player.character);
        if (companion) {
          const compCombatant = companionSystem.createCompanionCombatant(companion, player);
          this.companionCombatants.push(compCombatant);
        }
      }
    }

    // Create enemy combatants
    this.enemies = [];
    for (let i = 0; i < enemyDefs.length; i++) {
      const def = enemyDefs[i];
      const enemyData = dataStore.getById('enemies', def.id);
      if (enemyData) {
        this.enemies.push(createEnemyCombatant(enemyData, def.level || 1, i));
      } else {
        // Fallback: create a basic enemy from the def data
        this.enemies.push({
          id: `enemy_${def.id}_${i}`,
          name: def.name || def.id,
          type: 'enemy',
          hp: 20,
          maxHp: 20,
          mana: 0,
          maxMana: 0,
          ap: DEFAULT_AP,
          maxAp: DEFAULT_AP,
          attributes: { str: 10, agi: 10, int: 10, vit: 10, wis: 10, cha: 10 },
          initiative: 0,
          gridX: 0,
          gridY: 0,
          alive: true,
          statusEffects: [],
          character: null,
          behavior: 'melee',
          defense: 12,
          damage: 6,
          speed: 10,
          enemyData: null,
          level: def.level || 1,
          loot: null,
          size: 'medium',
        });
      }
    }

    if (this.players.length === 0 || this.enemies.length === 0) {
      this._addLog('Cannot start combat: need at least 1 player and 1 enemy.');
      return false;
    }

    // Initialize CombatAI with system references
    combatAI.init(this, skillSystem, tileEffectsSystem, statusEffectsSystem);

    // Apply difficulty scaling to enemies
    const partyLevel = party.partyLevel || 1;
    const regionLevelRange = this._currentRegionLevelRange || [1, 3];
    for (const enemy of this.enemies) {
      CombatAI.applyDifficultyModifiers(enemy, partyLevel, regionLevelRange);
    }

    // Calculate encounter difficulty
    const difficulty = CombatAI.calculateEncounterDifficulty(this.enemies, partyLevel);
    this._addLog(`Encounter difficulty: ${difficulty.difficulty} (CR ${difficulty.cr.toFixed(1)})`);

    // Initialize grid
    this._initGrid();

    // Place combatants
    this._placeCombatants();

    // Roll initiative (companions do NOT go in turn order; they auto-act after owner)
    this.rollInitiative([...this.players, ...this.enemies]);

    // Set state
    this.state = 'active';
    this.turnIndex = 0;
    this.turnCount = 1;

    const compCount = this.companionCombatants.length;
    this._addLog(`Combat begins! ${this.players.length} party members${compCount > 0 ? ` + ${compCount} companion(s)` : ''} vs ${this.enemies.length} enemies.`);

    eventBus.emit('combat:start', {
      players: this.players,
      enemies: this.enemies,
      companions: this.companionCombatants,
      grid: this.grid,
      rows: this.rows,
      cols: this.cols,
      turnOrder: this.turnOrder,
    });

    // Begin first turn
    this._beginTurn();

    return true;
  }

  /**
   * Initialize the combat grid based on combatant counts.
   */
  _initGrid() {
    const playerCount = this.players.length;
    const companionCount = this.companionCombatants.length;
    const enemyCount = this.enemies.length;

    // Rows = max(party + companions, enemy_count, 3) but capped at 6
    this.rows = Math.min(6, Math.max(playerCount + companionCount, enemyCount, 3));
    // 4 cols per side = 8 total
    this.cols = 8;

    // Create empty grid
    this.grid = [];
    for (let r = 0; r < this.rows; r++) {
      this.grid[r] = new Array(this.cols).fill(null);
    }
  }

  /**
   * Place combatants on the grid.
   * Players on left (cols 0-3), enemies on right (cols 4-7).
   */
  _placeCombatants() {
    // Place players in column 1 (second from left), spread across rows
    for (let i = 0; i < this.players.length; i++) {
      const row = i % this.rows;
      const col = 1;
      this.players[i].gridX = col;
      this.players[i].gridY = row;
      this.grid[row][col] = this.players[i];
    }

    // Place companions in column 2 (next to players), spread across rows
    for (let i = 0; i < this.companionCombatants.length; i++) {
      const row = i % this.rows;
      const col = 2;
      // Find an open spot if the row is taken
      if (this.grid[row][col] === null) {
        this.companionCombatants[i].gridX = col;
        this.companionCombatants[i].gridY = row;
        this.grid[row][col] = this.companionCombatants[i];
      } else {
        // Try other rows
        for (let r = 0; r < this.rows; r++) {
          if (this.grid[r][col] === null) {
            this.companionCombatants[i].gridX = col;
            this.companionCombatants[i].gridY = r;
            this.grid[r][col] = this.companionCombatants[i];
            break;
          }
        }
      }
    }

    // Place enemies in column 6 (second from right), spread across rows
    for (let i = 0; i < this.enemies.length; i++) {
      const row = i % this.rows;
      const col = 6;
      this.enemies[i].gridX = col;
      this.enemies[i].gridY = row;
      this.grid[row][col] = this.enemies[i];
    }
  }

  // ── Initiative ──────────────────────────────────────

  /**
   * Roll initiative for all combatants and sort turn order.
   * @param {object[]} combatants
   */
  rollInitiative(combatants) {
    for (const c of combatants) {
      const agiMod = this._getModifier(c.attributes.agi);
      const roll = this._rollD20();
      c.initiative = roll + agiMod;
      this._addLog(`${c.name} rolls initiative: ${roll} + ${agiMod} = ${c.initiative}`);
    }

    // Sort descending by initiative, tiebreak by agi then name
    this.turnOrder = [...combatants].sort((a, b) => {
      if (b.initiative !== a.initiative) return b.initiative - a.initiative;
      if (b.attributes.agi !== a.attributes.agi) return b.attributes.agi - a.attributes.agi;
      return a.name.localeCompare(b.name);
    });
  }

  // ── Turn Management ─────────────────────────────────

  /**
   * Get the current turn's combatant.
   * @returns {object|null}
   */
  getCurrentTurn() {
    if (this.turnOrder.length === 0) return null;
    return this.turnOrder[this.turnIndex] || null;
  }

  /**
   * Begin the current combatant's turn.
   */
  _beginTurn() {
    const current = this.getCurrentTurn();
    if (!current) return;

    // Skip dead combatants
    if (!current.alive) {
      this.nextTurn();
      return;
    }

    // Restore AP
    current.ap = current.maxAp;

    // Tick skill cooldowns
    skillSystem.tickCooldowns(current.id);

    // Apply tile effects if standing on an affected tile
    tileEffectsSystem.applyEffectsToUnit(current);

    // Process status effects at turn start
    this._processStatusEffects(current);

    // If stunned, skip turn
    if (current.ap <= 0) {
      this._addLog(`${current.name} is stunned and cannot act!`);
      this.nextTurn();
      return;
    }

    if (current.type === 'player') {
      // Check if auto-AI is enabled for this character
      const isAuto = combatAI.isAutoAI(current.id);
      this.state = isAuto ? 'autoTurn' : 'playerTurn';
      this._addLog(`--- ${current.name}'s turn (${current.ap} AP)${isAuto ? ' [AUTO]' : ''} ---`);
    } else {
      this.state = 'enemyTurn';
      this._addLog(`--- ${current.name}'s turn ---`);
    }

    eventBus.emit('combat:turn', {
      combatant: current,
      turnIndex: this.turnIndex,
      turnCount: this.turnCount,
      state: this.state,
    });

    // If enemy, run AI after a short delay (handled by scene)
    if (current.type === 'enemy') {
      eventBus.emit('combat:enemyTurnStart', { combatant: current });
    }

    // If auto-AI player, emit for scene to schedule AI
    if (current.type === 'player' && this.state === 'autoTurn') {
      eventBus.emit('combat:autoTurnStart', { combatant: current });
    }

    // If player, check for companion auto-action after turn ends
    // (This is handled by endTurn -> nextTurn flow and the scene's companion scheduling)
    if (current.type === 'player') {
      // Find companion for this player
      const companion = this.companionCombatants.find(
        c => c.ownerId === current.id && c.alive
      );
      if (companion) {
        eventBus.emit('combat:companionReady', {
          companion,
          owner: current,
        });
      }
    }
  }

  /**
   * Advance to the next combatant in turn order.
   */
  nextTurn() {
    this.turnIndex++;

    // Wrap around and increment turn counter
    if (this.turnIndex >= this.turnOrder.length) {
      this.turnIndex = 0;
      this.turnCount++;
      this._addLog(`=== Round ${this.turnCount} ===`);

      // Tick tile effects at end of round
      tileEffectsSystem.tickEffects();
    }

    // Check if combat is over
    if (this.checkCombatEnd()) return;

    this._beginTurn();
  }

  // ── Actions ─────────────────────────────────────────

  /**
   * Move a unit to a target grid position.
   * @param {object} unit - The combatant to move
   * @param {number} x - Target column
   * @param {number} y - Target row
   * @returns {{ success: boolean, message: string }}
   */
  moveUnit(unit, x, y) {
    if (!unit.alive) return { success: false, message: 'Unit is dead.' };
    if (unit.ap < AP_COSTS.move) return { success: false, message: 'Not enough AP to move.' };

    // Check movement is to an adjacent tile (orthogonal only)
    const dx = Math.abs(x - unit.gridX);
    const dy = Math.abs(y - unit.gridY);
    if (dx + dy !== 1) {
      return { success: false, message: 'Can only move to an adjacent tile.' };
    }

    // Check bounds
    if (y < 0 || y >= this.rows || x < 0 || x >= this.cols) {
      return { success: false, message: 'Target is out of bounds.' };
    }

    // Check if tile is occupied
    if (this.grid[y][x] !== null) {
      return { success: false, message: 'Tile is occupied.' };
    }

    // Check rooted status
    const isRooted = unit.statusEffects.some(e => e.id === 'rooted');
    if (isRooted) return { success: false, message: `${unit.name} is rooted and cannot move.` };

    // Move
    this.grid[unit.gridY][unit.gridX] = null;
    unit.gridX = x;
    unit.gridY = y;
    this.grid[y][x] = unit;

    // Deduct AP (check for slowed)
    let moveCost = AP_COSTS.move;
    const slowed = unit.statusEffects.find(e => e.id === 'slowed');
    if (slowed) moveCost += 1;
    unit.ap -= moveCost;

    // Apply tile effects at new position
    tileEffectsSystem.applyEffectsToUnit(unit);

    const msg = `${unit.name} moves to (${x}, ${y}). [${unit.ap} AP left]`;
    this._addLog(msg);

    eventBus.emit('combat:move', { unit, x, y, apRemaining: unit.ap });

    return { success: true, message: msg };
  }

  /**
   * Perform an attack from attacker to target.
   * @param {object} attacker
   * @param {object} target
   * @param {'basic'|'strong'|'quick'} type
   * @returns {{ success: boolean, message: string, hit: boolean, damage: number, critical: boolean }}
   */
  attack(attacker, target, type = 'basic') {
    const apCost = AP_COSTS[type] || AP_COSTS.basic;

    if (!attacker.alive) return { success: false, message: 'Attacker is dead.', hit: false, damage: 0, critical: false };
    if (!target.alive) return { success: false, message: 'Target is already dead.', hit: false, damage: 0, critical: false };
    if (attacker.ap < apCost) return { success: false, message: `Not enough AP (need ${apCost}, have ${attacker.ap}).`, hit: false, damage: 0, critical: false };

    // Check range: for basic melee, must be adjacent or within 2 tiles
    const dist = Math.abs(attacker.gridX - target.gridX) + Math.abs(attacker.gridY - target.gridY);
    const maxRange = (attacker.behavior === 'ranged') ? 6 : 2;
    if (dist > maxRange) {
      return { success: false, message: 'Target is out of range.', hit: false, damage: 0, critical: false };
    }

    // Check untargetable (invisible)
    const isUntargetable = target.statusEffects.some(e =>
      e.effects?.some(ef => ef.type === 'untargetable')
    );
    if (isUntargetable) {
      return { success: false, message: `${target.name} cannot be targeted.`, hit: false, damage: 0, critical: false };
    }

    // Deduct AP
    attacker.ap -= apCost;

    // Roll to hit
    const hitRoll = this._rollD20();
    const agiMod = this._getModifier(attacker.attributes.agi);
    const totalHit = hitRoll + agiMod;
    const defenseDC = target.defense;

    let hit = false;
    let critical = false;
    let damageDealt = 0;
    let resultType = 'miss';

    if (hitRoll === 1) {
      // Natural 1: always miss
      resultType = 'miss';
    } else if (hitRoll === 20) {
      // Natural 20: always crit
      hit = true;
      critical = true;
      resultType = 'critical';
    } else if (totalHit >= defenseDC + 10) {
      // Crit threshold
      hit = true;
      critical = true;
      resultType = 'critical';
    } else if (totalHit >= defenseDC) {
      // Hit
      hit = true;
      resultType = 'hit';
    } else if (totalHit >= defenseDC - 5) {
      // Graze
      hit = true;
      resultType = 'graze';
    } else {
      resultType = 'miss';
    }

    if (hit) {
      // Calculate base damage
      const strMod = this._getModifier(attacker.attributes.str);
      const multiplier = ATTACK_MULTIPLIERS[type] || 1.0;
      let baseDmg = Math.max(1, attacker.damage + strMod);
      damageDealt = Math.floor(baseDmg * multiplier);

      // Graze = 50% damage
      if (resultType === 'graze') {
        damageDealt = Math.max(1, Math.floor(damageDealt * 0.5));
      }

      // Critical = 150% damage
      if (critical) {
        damageDealt = Math.floor(damageDealt * 1.5);
      }

      // Check frozen (damage amplify)
      const frozenEffect = target.statusEffects.find(e => e.id === 'frozen');
      if (frozenEffect) {
        damageDealt = Math.floor(damageDealt * 1.5);
        // Remove frozen (shatter)
        target.statusEffects = target.statusEffects.filter(e => e.id !== 'frozen');
        this._addLog(`The ice shatters! ${target.name} takes extra damage.`);
      }

      // Check defending
      const defending = target.statusEffects.find(e => e.id === 'defending');
      if (defending) {
        damageDealt = Math.max(1, Math.floor(damageDealt * 0.5));
      }

      // Check shielded (absorb)
      const shielded = target.statusEffects.find(e => e.id === 'shielded');
      if (shielded) {
        const absorbValue = shielded._absorbRemaining || 20;
        const absorbed = Math.min(damageDealt, absorbValue);
        damageDealt -= absorbed;
        shielded._absorbRemaining = absorbValue - absorbed;
        if (shielded._absorbRemaining <= 0) {
          target.statusEffects = target.statusEffects.filter(e => e.id !== 'shielded');
          this._addLog(`${target.name}'s shield breaks!`);
        }
      }

      // Apply damage
      damageDealt = Math.max(0, damageDealt);
      target.hp -= damageDealt;

      // Clamp HP
      target.hp = Math.max(0, target.hp);

      // Check death
      if (target.hp <= 0) {
        target.alive = false;
        this._addLog(`${target.name} has been defeated!`);
        eventBus.emit('combat:kill', { attacker, target });
      }
    }

    // Build message
    const typeLabel = type === 'basic' ? '' : ` (${type})`;
    let msg;
    if (resultType === 'miss') {
      msg = `${attacker.name} attacks${typeLabel} ${target.name}: MISS (rolled ${hitRoll}+${agiMod}=${totalHit} vs DC ${defenseDC})`;
    } else if (resultType === 'graze') {
      msg = `${attacker.name} grazes${typeLabel} ${target.name} for ${damageDealt} damage. (rolled ${hitRoll}+${agiMod}=${totalHit} vs DC ${defenseDC})`;
    } else if (resultType === 'critical') {
      msg = `${attacker.name} CRITS${typeLabel} ${target.name} for ${damageDealt} damage! (rolled ${hitRoll})`;
    } else {
      msg = `${attacker.name} hits${typeLabel} ${target.name} for ${damageDealt} damage. (rolled ${hitRoll}+${agiMod}=${totalHit} vs DC ${defenseDC})`;
    }
    this._addLog(msg);

    const eventName = hit ? 'combat:hit' : 'combat:miss';
    eventBus.emit(eventName, {
      attacker,
      target,
      type,
      hitRoll,
      totalHit,
      defenseDC,
      resultType,
      damage: damageDealt,
      critical,
    });

    eventBus.emit('combat:attack', {
      attacker,
      target,
      type,
      hit,
      damage: damageDealt,
      critical,
      resultType,
    });

    return { success: true, message: msg, hit, damage: damageDealt, critical };
  }

  /**
   * Use a consumable item.
   * @param {object} unit
   * @param {object} item
   * @param {object} [target] - target combatant (for healing items, etc.)
   * @returns {{ success: boolean, message: string }}
   */
  useItem(unit, item, target = null) {
    if (!unit.alive) return { success: false, message: 'Unit is dead.' };
    if (unit.ap < AP_COSTS.item) return { success: false, message: 'Not enough AP to use item.' };

    unit.ap -= AP_COSTS.item;

    const effectTarget = target || unit;
    let msg = `${unit.name} uses ${item.name || 'item'}`;

    const effect = item.effect || {};
    if (effect.type === 'heal' || item.type === 'healing' || item.id === 'health_potion') {
      const healAmount = effect.amount || item.healAmount || 20;
      effectTarget.hp = Math.min(effectTarget.maxHp, effectTarget.hp + healAmount);
      msg += ` on ${effectTarget.name}, restoring ${healAmount} HP.`;
    } else if (effect.type === 'damage') {
      const dmg = effect.amount || 20;
      effectTarget.hp = Math.max(0, effectTarget.hp - dmg);
      msg += ` on ${effectTarget.name}, dealing ${dmg} damage.`;
      if (effectTarget.hp <= 0) {
        effectTarget.alive = false;
        msg += ` ${effectTarget.name} is defeated!`;
      }
    } else if (effect.type === 'cureStatus') {
      effectTarget.statusEffects = effectTarget.statusEffects.filter(e => e.id !== effect.statusId);
      msg += ` on ${effectTarget.name}, curing ${effect.statusId}.`;
    } else {
      msg += '. [Item effect not implemented]';
    }

    this._addLog(msg);

    eventBus.emit('combat:useItem', { unit, item, target: effectTarget });

    return { success: true, message: msg };
  }

  /**
   * End the current unit's turn (spend remaining AP).
   */
  endTurn() {
    const current = this.getCurrentTurn();
    if (!current) return;

    current.ap = 0;
    this._addLog(`${current.name} ends their turn.`);

    eventBus.emit('combat:endTurn', { combatant: current });

    this.nextTurn();
  }

  // ── Range Calculations ──────────────────────────────

  /**
   * Get all tiles a unit can move to with their remaining AP.
   * Uses BFS for pathfinding within AP budget.
   * @param {object} unit
   * @returns {Array<{x: number, y: number, cost: number}>}
   */
  getMovementRange(unit) {
    if (!unit.alive) return [];

    // Check rooted
    const isRooted = unit.statusEffects.some(e => e.id === 'rooted');
    if (isRooted) return [];

    const moveCost = AP_COSTS.move + (unit.statusEffects.some(e => e.id === 'slowed') ? 1 : 0);
    const maxMoves = Math.floor(unit.ap / moveCost);
    if (maxMoves <= 0) return [];

    const reachable = [];
    const visited = new Set();
    const queue = [{ x: unit.gridX, y: unit.gridY, cost: 0 }];
    visited.add(`${unit.gridX},${unit.gridY}`);

    const directions = [
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 },
    ];

    while (queue.length > 0) {
      const { x, y, cost } = queue.shift();

      for (const { dx, dy } of directions) {
        const nx = x + dx;
        const ny = y + dy;
        const key = `${nx},${ny}`;
        const newCost = cost + 1;

        if (nx < 0 || nx >= this.cols || ny < 0 || ny >= this.rows) continue;
        if (visited.has(key)) continue;
        if (newCost > maxMoves) continue;
        if (this.grid[ny][nx] !== null) continue;

        visited.add(key);
        reachable.push({ x: nx, y: ny, cost: newCost });
        queue.push({ x: nx, y: ny, cost: newCost });
      }
    }

    return reachable;
  }

  /**
   * Find a path from a unit's current position to a target tile using BFS.
   * Returns an array of {x, y} steps (excluding the start position).
   * @param {object} unit
   * @param {number} targetX
   * @param {number} targetY
   * @returns {Array<{x: number, y: number}>}
   */
  findPath(unit, targetX, targetY) {
    const visited = new Set();
    const queue = [{ x: unit.gridX, y: unit.gridY, path: [] }];
    visited.add(`${unit.gridX},${unit.gridY}`);

    const directions = [
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 },
    ];

    while (queue.length > 0) {
      const { x, y, path } = queue.shift();

      for (const { dx, dy } of directions) {
        const nx = x + dx;
        const ny = y + dy;
        const key = `${nx},${ny}`;

        if (nx < 0 || nx >= this.cols || ny < 0 || ny >= this.rows) continue;
        if (visited.has(key)) continue;
        if (this.grid[ny][nx] !== null && !(nx === targetX && ny === targetY)) continue;

        const newPath = [...path, { x: nx, y: ny }];

        if (nx === targetX && ny === targetY) {
          return newPath;
        }

        visited.add(key);
        queue.push({ x: nx, y: ny, path: newPath });
      }
    }

    return []; // No path found
  }

  /**
   * Get all tiles a unit can attack from its current position.
   * @param {object} unit
   * @returns {Array<{x: number, y: number}>}
   */
  getAttackRange(unit) {
    if (!unit.alive) return [];
    if (unit.ap < AP_COSTS.quick) return []; // Need at least 1 AP for quick attack

    const maxRange = (unit.behavior === 'ranged') ? 6 : 2;
    const targets = [];

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const dist = Math.abs(c - unit.gridX) + Math.abs(r - unit.gridY);
        if (dist > 0 && dist <= maxRange) {
          const occupant = this.grid[r][c];
          if (occupant && occupant.alive && occupant.type !== unit.type) {
            targets.push({ x: c, y: r, combatant: occupant });
          }
        }
      }
    }

    return targets;
  }

  // ── Status Effects ──────────────────────────────────

  /**
   * Process status effects at the start of a combatant's turn.
   * @param {object} unit
   */
  _processStatusEffects(unit) {
    const expiring = [];

    for (const effect of unit.statusEffects) {
      // Process per-turn effects
      if (effect.effects) {
        for (const e of effect.effects) {
          if (e.type === 'damagePerTurn') {
            const dmg = e.value || 5;
            unit.hp -= dmg;
            unit.hp = Math.max(0, unit.hp);
            this._addLog(`${unit.name} takes ${dmg} ${e.damageType || ''} damage from ${effect.name}.`);
            if (unit.hp <= 0) {
              unit.alive = false;
              this._addLog(`${unit.name} has been defeated by ${effect.name}!`);
              eventBus.emit('combat:kill', { attacker: null, target: unit });
            }
          }
          if (e.type === 'healPerTurn') {
            const heal = e.value || 5;
            unit.hp = Math.min(unit.maxHp, unit.hp + heal);
            this._addLog(`${unit.name} regenerates ${heal} HP.`);
          }
          if (e.type === 'loseAllAP') {
            unit.ap = 0;
          }
        }
      }

      // Decrement duration
      if (effect.duration > 0) {
        effect.duration--;
        if (effect.duration <= 0) {
          expiring.push(effect);
        }
      }
    }

    // Remove expired effects
    for (const exp of expiring) {
      unit.statusEffects = unit.statusEffects.filter(e => e !== exp);
      this._addLog(`${exp.name} wears off on ${unit.name}.`);
    }
  }

  // ── Enemy AI ────────────────────────────────────────

  /**
   * Execute AI logic for an enemy combatant.
   * Delegates to the sophisticated CombatAI system.
   * @param {object} enemy
   * @returns {object[]} Array of actions taken
   */
  runEnemyAI(enemy) {
    return combatAI.runEnemyAI(enemy);
  }

  /**
   * Execute auto-AI for a player character.
   * @param {object} player
   * @returns {object[]} Array of actions taken
   */
  runPartyAI(player) {
    return combatAI.runPartyAI(player);
  }

  /**
   * Check if a player character has auto-AI enabled.
   * @param {string} characterId
   * @returns {boolean}
   */
  isAutoAI(characterId) {
    return combatAI.isAutoAI(characterId);
  }

  /**
   * Toggle auto-AI for a player character.
   * @param {string} characterId
   * @param {boolean} [enabled]
   * @returns {boolean}
   */
  toggleAutoAI(characterId, enabled) {
    return combatAI.toggleAutoAI(characterId, enabled);
  }

  /**
   * Get adjacent combatants of the opposing team.
   * @param {object} unit
   * @returns {object[]}
   */
  _getAdjacentEnemies(unit) {
    const targets = [];
    const directions = [
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 },
    ];

    for (const { dx, dy } of directions) {
      const nx = unit.gridX + dx;
      const ny = unit.gridY + dy;
      if (nx < 0 || nx >= this.cols || ny < 0 || ny >= this.rows) continue;
      const occupant = this.grid[ny][nx];
      if (occupant && occupant.alive && occupant.type !== unit.type) {
        targets.push(occupant);
      }
    }

    // For ranged enemies, also check further tiles
    if (unit.behavior === 'ranged') {
      for (let r = 0; r < this.rows; r++) {
        for (let c = 0; c < this.cols; c++) {
          const dist = Math.abs(c - unit.gridX) + Math.abs(r - unit.gridY);
          if (dist > 1 && dist <= 6) {
            const occupant = this.grid[r][c];
            if (occupant && occupant.alive && occupant.type !== unit.type) {
              targets.push(occupant);
            }
          }
        }
      }
    }

    return targets;
  }

  /**
   * AI: Move one step toward the nearest player character.
   * @param {object} enemy
   * @returns {boolean} true if moved
   */
  _aiMoveTowardPlayer(enemy) {
    // Find nearest alive player
    let nearest = null;
    let nearestDist = Infinity;

    for (const player of this.players) {
      if (!player.alive) continue;
      const dist = Math.abs(player.gridX - enemy.gridX) + Math.abs(player.gridY - enemy.gridY);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = player;
      }
    }

    if (!nearest) return false;

    // Choose the best adjacent tile to move to (closest to target)
    const directions = [
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 },
    ];

    let bestDir = null;
    let bestDist = nearestDist;

    for (const { dx, dy } of directions) {
      const nx = enemy.gridX + dx;
      const ny = enemy.gridY + dy;
      if (nx < 0 || nx >= this.cols || ny < 0 || ny >= this.rows) continue;
      if (this.grid[ny][nx] !== null) continue;

      const dist = Math.abs(nearest.gridX - nx) + Math.abs(nearest.gridY - ny);
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = { dx, dy };
      }
    }

    if (bestDir) {
      const result = this.moveUnit(enemy, enemy.gridX + bestDir.dx, enemy.gridY + bestDir.dy);
      return result.success;
    }

    return false;
  }

  // ── Combat End ──────────────────────────────────────

  /**
   * Check if combat has ended.
   * @returns {boolean} true if combat ended
   */
  checkCombatEnd() {
    // Kill companions whose owners have died
    for (const comp of this.companionCombatants) {
      if (comp.alive && comp.ownerId) {
        const owner = this.players.find(p => p.id === comp.ownerId);
        if (owner && !owner.alive) {
          comp.hp = 0;
          comp.alive = false;
          this._addLog(`${comp.name} fades away as its master falls.`);
          eventBus.emit('combat:kill', { attacker: null, target: comp });
        }
      }
    }

    const playersAlive = this.players.some(p => p.alive);
    const enemiesAlive = this.enemies.some(e => e.alive);

    if (!enemiesAlive) {
      this.state = 'victory';
      this._rewards = this.getRewards();
      this._addLog('*** VICTORY! All enemies defeated. ***');
      eventBus.emit('combat:victory', { rewards: this._rewards });

      // Sync HP back to party characters
      for (const p of this.players) {
        if (p.character) {
          p.character.hp = p.hp;
        }
      }

      return true;
    }

    if (!playersAlive) {
      this.state = 'defeat';
      this._addLog('*** DEFEAT! All party members have fallen. ***');
      eventBus.emit('combat:defeat', {});
      return true;
    }

    return false;
  }

  /**
   * Calculate combat rewards.
   * @returns {{ xp: number, gold: number, items: object[] }}
   */
  getRewards() {
    let totalXP = 0;
    let totalGold = 0;
    const items = [];
    const lootRolls = [];

    const partyLevel = this._party?.partyLevel || 1;

    for (const enemy of this.enemies) {
      // XP based on level
      const level = enemy.level || 1;
      totalXP += 10 * level + Math.floor(Math.random() * 5 * level);

      // Use LootSystem for gold and item generation
      if (enemy.loot) {
        const loot = lootSystem.generateLoot(enemy.loot, partyLevel);
        totalGold += loot.gold;

        for (const item of loot.items) {
          items.push(item);
          // Add to party inventory
          inventorySystem.addItem(item);
        }
        lootRolls.push(...loot.rolls);
      }
    }

    return { xp: totalXP, gold: totalGold, items, lootRolls };
  }

  // ── Logging ─────────────────────────────────────────

  /**
   * Add a message to the combat log.
   * @param {string} msg
   */
  _addLog(msg) {
    this.log.push(msg);
    eventBus.emit('combat:log', { message: msg });
  }

  /**
   * Get all combat log messages.
   * @returns {string[]}
   */
  getLog() {
    return [...this.log];
  }

  // ── Utility ─────────────────────────────────────────

  /**
   * Get the combatant at a grid position.
   * @param {number} x
   * @param {number} y
   * @returns {object|null}
   */
  getUnitAt(x, y) {
    if (y < 0 || y >= this.rows || x < 0 || x >= this.cols) return null;
    return this.grid[y][x];
  }

  /**
   * Check if a tile is walkable (in bounds, unoccupied).
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  isTileWalkable(x, y) {
    if (y < 0 || y >= this.rows || x < 0 || x >= this.cols) return false;
    return this.grid[y][x] === null;
  }

  /**
   * Get the Manhattan distance between two combatants.
   * @param {object} a
   * @param {object} b
   * @returns {number}
   */
  getDistance(a, b) {
    return Math.abs(a.gridX - b.gridX) + Math.abs(a.gridY - b.gridY);
  }

  /**
   * Reset the combat system to idle state.
   */
  reset() {
    this.grid = [];
    this.rows = 0;
    this.cols = 0;
    this.turnOrder = [];
    this.turnIndex = 0;
    this.players = [];
    this.enemies = [];
    this.companionCombatants = [];
    this.state = 'idle';
    this.log = [];
    this.turnCount = 0;
    this._party = null;
    this._rewards = null;
    this._currentRegionLevelRange = [1, 3];
    combatAI.destroy();
  }
}

// Singleton
const combatSystem = new CombatSystem();
export default combatSystem;
export { CombatSystem, AP_COSTS, DEFAULT_AP, ATTACK_MULTIPLIERS, skillSystem, statusEffectsSystem, tileEffectsSystem, companionSystem, combatAI };
